import { Injectable } from '@angular/core';
import { ConnectService } from '@epicuro-next/data/connect';
import { ConnectionStatus } from '@epicuro-next/models/users';
import { ConnectNotificationType } from '@epicuro-next/platform/configuration';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import { selectConnectToken } from '@epicuro-next/state/connect-token-state';
import * as fromSignalrState from '@epicuro-next/state/signalr-state';
import { selectIsSalesRep } from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import * as actions from '../actions';
import { selectConnectIsEnabled } from '../selectors';

@Injectable()
export class ConnectEffects {
  public connectEnabled$ = this.store.pipe(
    select(selectConnectIsEnabled),
    filter(Boolean),
  );

  constructor(
    private actions$: Actions,
    private connectService: ConnectService,
    private store: Store,
  ) {}

  public initConnectSuccess$ = createEffect(() =>
    this.connectEnabled$.pipe(map(() => actions.initConnectSuccess())),
  );

  public initializeConnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.initConnectSuccess),
      map(() => actions.requestNotificationPermission()),
    ),
  );

  public refreshAccountContacts$ = createEffect(() =>
    combineLatest([
      this.store.select(selectConnectToken),
      this.store.select(selectActiveAccountId),
    ]).pipe(
      filter(
        ([connectToken, accountId]) =>
          isNonNullish(connectToken) && isNonNullish(accountId),
      ),
      switchMap(() =>
        this.connectEnabled$.pipe(map(() => actions.loadUserContactsState())),
      ),
    ),
  );

  public finalizeCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.finalizeCall),
      map(() =>
        actions.setUserConnectionStatus({
          status: ConnectionStatus.AVAILABLE,
        }),
      ),
    ),
  );

  public setUserConnectionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setUserConnectionStatus),
      concatLatestFrom(() => [this.store.pipe(select(selectConnectIsEnabled))]),
      filter(([, connectIsEnabled]) => connectIsEnabled),
      map(([{ status }]) =>
        fromSignalrState.sendSignalrMessage({
          methodName: ConnectNotificationType.SetConnectionStatus,
          params: [status],
        }),
      ),
    ),
  );

  public loadUserContactsState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadUserContactsState),
      concatLatestFrom(() => [
        this.store.pipe(select(selectIsSalesRep)),
        this.store.pipe(select(selectConnectIsEnabled)),
      ]),
      filter(([, , connectIsEnabled]) => connectIsEnabled),
      switchMap(([, isSalesRep]) =>
        this.connectService.loadUserContacts().pipe(
          map((contacts) =>
            isSalesRep
              ? contacts.filter((contact) => contact.accounts.length > 0)
              : contacts,
          ),
          map((contacts) =>
            actions.loadUserContactsStateSuccess({
              contacts,
            }),
          ),
          catchError(() => of(actions.loadUserContactsStateFailure())),
        ),
      ),
    ),
  );

  public setCurrentUserContactState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setCurrentUserContactState),
      switchMap(() => [actions.initializeCall(), actions.createVideoCall()]),
    ),
  );
}
